export type EmbeddedMediaKind = 'image' | 'video';

export type MessageContentSegment =
  | { type: 'text'; value: string }
  | { type: EmbeddedMediaKind; url: string; alt?: string };

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'm4v']);

const MARKDOWN_MEDIA_PATTERN = /!\[([^\]]*)]\((https?:\/\/[^)\s]+)\)/gi;
const BARE_MEDIA_URL_PATTERN =
  /https?:\/\/[^\s<>"'`[\](){}]+?\.(?:jpe?g|png|gif|webp|bmp|svg|mp4|webm|mov|m4v)(?:\?[^\s<>"'`[\](){}]*)?/gi;

const getUrlPathExtension = (url: string): string | null => {
  try {
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split('/').pop() || '';
    const extension = lastSegment.includes('.')
      ? lastSegment.slice(lastSegment.lastIndexOf('.') + 1).toLowerCase()
      : '';
    return extension || null;
  } catch {
    const withoutQuery = url.split('?')[0] || '';
    const lastSegment = withoutQuery.split('/').pop() || '';
    const extension = lastSegment.includes('.')
      ? lastSegment.slice(lastSegment.lastIndexOf('.') + 1).toLowerCase()
      : '';
    return extension || null;
  }
};

export const getEmbeddedMediaKindFromUrl = (url: string): EmbeddedMediaKind | null => {
  const extension = getUrlPathExtension(url);
  if (!extension) {
    return null;
  }

  if (IMAGE_EXTENSIONS.has(extension)) {
    return 'image';
  }

  if (VIDEO_EXTENSIONS.has(extension)) {
    return 'video';
  }

  return null;
};

const trimUrlEdgePunctuation = (url: string): string => {
  return url.replace(/[),.]+$/g, '');
};

const normalizeTextSegment = (value: string): string => {
  return value
    .replace(/^\s*\d+\.\s*(?=\n|$)/gm, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

interface MediaMatch {
  index: number;
  length: number;
  kind: EmbeddedMediaKind;
  url: string;
  alt?: string;
}

const collectMarkdownMediaMatches = (text: string): MediaMatch[] => {
  const matches: MediaMatch[] = [];
  const regex = new RegExp(MARKDOWN_MEDIA_PATTERN.source, 'gi');
  let match: RegExpExecArray | null = regex.exec(text);

  while (match) {
    const url = trimUrlEdgePunctuation(match[2] || '');
    const kind = getEmbeddedMediaKindFromUrl(url) || 'image';
    matches.push({
      index: match.index,
      length: match[0].length,
      kind,
      url,
      alt: (match[1] || '').trim() || undefined,
    });
    match = regex.exec(text);
  }

  return matches;
};

const collectBareMediaUrlMatches = (text: string, occupiedRanges: MediaMatch[]): MediaMatch[] => {
  const matches: MediaMatch[] = [];
  const regex = new RegExp(BARE_MEDIA_URL_PATTERN.source, 'gi');
  let match: RegExpExecArray | null = regex.exec(text);

  while (match) {
    const rawUrl = trimUrlEdgePunctuation(match[0] || '');
    const kind = getEmbeddedMediaKindFromUrl(rawUrl);
    const start = match.index;
    const end = start + match[0].length;
    const overlaps = occupiedRanges.some(item => start < item.index + item.length && end > item.index);

    if (kind && !overlaps) {
      matches.push({
        index: start,
        length: match[0].length,
        kind,
        url: rawUrl,
      });
    }

    match = regex.exec(text);
  }

  return matches;
};

export const parseMessageContentWithEmbeddedMedia = (text: string): MessageContentSegment[] => {
  if (!text) {
    return [];
  }

  const markdownMatches = collectMarkdownMediaMatches(text);
  const bareMatches = collectBareMediaUrlMatches(text, markdownMatches);
  const mediaMatches = [...markdownMatches, ...bareMatches].sort((left, right) => left.index - right.index);

  if (mediaMatches.length === 0) {
    return [{ type: 'text', value: text }];
  }

  const segments: MessageContentSegment[] = [];
  let cursor = 0;

  for (const media of mediaMatches) {
    if (media.index < cursor) {
      continue;
    }

    if (media.index > cursor) {
      const textValue = normalizeTextSegment(text.slice(cursor, media.index));
      if (textValue) {
        segments.push({ type: 'text', value: textValue });
      }
    }

    segments.push({
      type: media.kind,
      url: media.url,
      alt: media.alt,
    });

    cursor = media.index + media.length;
  }

  if (cursor < text.length) {
    const trailing = normalizeTextSegment(text.slice(cursor));
    if (trailing) {
      segments.push({ type: 'text', value: trailing });
    }
  }

  return segments.length > 0 ? segments : [{ type: 'text', value: text }];
};

export const hasEmbeddedMediaInText = (text: string): boolean => {
  return parseMessageContentWithEmbeddedMedia(text).some(segment => segment.type === 'image' || segment.type === 'video');
};
